import CloseIcon from '@mui/icons-material/Close';

import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { ChangeEvent, SyntheticEvent, useCallback, useState } from 'react';
import {
  ALLOWED_EXTENSIONS,
  EXTENSION_TO_CONTENT_TYPE,
  MAX_BATCH_UPLOAD_SIZE,
  MaxDataCollectionFileSize,
} from '../../../constants/file-upload.constants';
import { useFileDragDrop } from '../../../hooks/useFileDragDrop';
import { useResponseFileName } from '../../../pages/KPI/components/Sections/FormItem/FileUploadField/useResponseFileName';
import { convertFileSize, validateFileExtension } from '../../../pages/KPI/utils';
import { S3URLRequestResponse, UploadedFileMeta, uploadFile } from '../../../services/queries/FileQueries';
import { fetchKPIRequestUploadedFileFromS3 } from '../../../services/queries/KPIRequestsQueries';
import { colors } from '../../../theme/colors';
import { IFormFieldUploadMeta } from '../../../view-models/form.view-model';
import { IBaseFieldProps } from './types';

/**
 * Standalone mode: Allows this component to be used outside of the form system.
 *
 * WHY STANDALONE?
 * Originally, this component was tightly coupled to the form system (formField/formProps).
 * However, we needed to reuse the same upload UI and logic in contexts that don't have
 * a form structure (e.g., standalone file pickers, deal document uploads, etc.).
 *
 * Instead of duplicating the component, we added a standalone mode that:
 * - Bypasses form integration entirely
 * - Provides its own value/onChange handlers
 * - Allows custom upload functions (onUpload, onBatchUpload)
 * - Supports different UI variants (default, minimal)
 *
 * This keeps the component DRY while supporting both form and non-form use cases.
 */
interface StandaloneProps {
  value?: UploadedFileMeta[];
  onChange?: (files: UploadedFileMeta[] | UploadedFileMeta | null) => void;
  onUploadSuccess?: () => void;
  /** Single file upload function - uploads one file at a time sequentially */
  onUpload?: (file: File) => Promise<UploadedFileMeta>;
  /** Batch upload function - uploads multiple files in a single optimized operation */
  onBatchUpload?: (files: File[]) => Promise<UploadedFileMeta[]>;
  multiple?: boolean;
  disabled?: boolean;
  error?: string;
  maxFileSizeMb?: number;
  variant?: 'default' | 'minimal';
}

/**
 * Extended props interface - use union type for form vs standalone mode.
 * This ensures type safety: component is either in form mode OR standalone mode, never both.
 */
type IExtendedFieldProps<T> =
  | IBaseFieldProps<T>
  | {
      formField?: never;
      formProps?: never;
      standalone: StandaloneProps;
    };

export { MaxDataCollectionFileSize };

const InputFileWrapper = styled('div')`
  position: relative;
  padding: 1.5rem;
  width: 100%;
  display: grid;
  align-items: center;
  background: transparent;
  transition: all 0.3s;
  filter: none;
  &:hover:not([disabled]) {
    background: ${colors.neutral[10]}22;
    filter: brightness(0.85);
  }

  input[type=file], /* FF, IE7+, chrome (except button) */
  input[type=file]::-webkit-file-upload-button {
    /* chromes and blink button */
    cursor: pointer;
  }
  & input {
    all: unset;
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    height: 100%;
    width: 100%;
  }
`;

const preventDefaults = {
  onDragOver: (e: SyntheticEvent) => {
    e.preventDefault();
  },
  onDrop: (e: SyntheticEvent) => {
    e.preventDefault();
  },
};

export function FieldKPIFileUpload3(props: IExtendedFieldProps<IFormFieldUploadMeta>) {
  const { formField, formProps } = props;
  const standalone = 'standalone' in props ? props.standalone : undefined;

  /**
   * Mode Detection:
   * - FORM MODE: When formField/formProps are provided (original use case)
   * - STANDALONE MODE: When only 'standalone' prop is provided (reusable outside forms)
   *
   * The union type in IExtendedFieldProps ensures these modes are mutually exclusive.
   */
  const isStandalone = !formField && !formProps && !!standalone;

  // Check for minimal variant
  const isMinimal = isStandalone && standalone?.variant === 'minimal';

  // Extract props based on mode
  const disabled = isStandalone ? (standalone?.disabled ?? false) : formProps?.disabled;
  const onChangeCallback = isStandalone ? standalone?.onChange : formProps?.onChange;
  const companyId = isStandalone ? undefined : formField?.rendererMeta?.companyId;
  const multiple = isStandalone
    ? (standalone?.multiple ?? false)
    : (formField?.rendererMeta?.multiple ?? false);
  const maxFileSizeMb = isStandalone
    ? (standalone?.maxFileSizeMb ?? MaxDataCollectionFileSize)
    : (formField?.rendererMeta?.maxFileSizeMb ?? MaxDataCollectionFileSize);

  const fileMeta = isStandalone
    ? standalone?.value
    : (formProps?.value as UploadedFileMeta | UploadedFileMeta[] | null);
  const fieldLabel = isStandalone ? undefined : formField?.label;

  const [value, setValue] = useState<UploadedFileMeta[]>(() => {
    if (!fileMeta) {
      return [];
    }
    if (Array.isArray(fileMeta)) {
      return fileMeta;
    }
    return [fileMeta];
  });
  const theme = useTheme();
  const { colors } = theme;
  const [error, setError] = useState<string | null>(standalone?.error ?? null);
  const [isUploading, setIsUploading] = useState(false);

  // Default upload function for form mode
  const defaultUploadFunction = useCallback(
    async (file: File): Promise<UploadedFileMeta> => {
      if (!companyId) {
        throw new Error('companyId required for form mode');
      }

      const response = await uploadFile(companyId, file);
      const data = response?.data as (S3URLRequestResponse & { getUrl?: string }) | null;
      const error = response?.error;

      if (error || !data) {
        throw new Error(error || 'Upload failed');
      }

      return {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type,
        ...data,
      };
    },
    [companyId]
  );

  // Use custom upload function in standalone mode, default otherwise
  const uploadFunction = isStandalone && standalone?.onUpload ? standalone.onUpload : defaultUploadFunction;

  const doUpload = useCallback(
    async (file: File | File[]) => {
      setError(null);
      const initialValue = [...value];
      let filesToUpload = Array.isArray(file) ? file : [file];
      let errorText = '';

      // Validate batch size to prevent DoS attacks and resource exhaustion
      if (filesToUpload.length === 0) {
        setError('No files selected for upload');
        return;
      }

      if (filesToUpload.length > MAX_BATCH_UPLOAD_SIZE) {
        setError(
          `Cannot upload more than ${MAX_BATCH_UPLOAD_SIZE} files at once. Please select ${MAX_BATCH_UPLOAD_SIZE} or fewer files.`
        );
        return;
      }

      const { validTypeFiles, invalidTypeFiles: invalidTypeErrors } = filesToUpload.reduce(
        (acc, f) => {
          if (validateFileExtension(f.name, ALLOWED_EXTENSIONS)) {
            acc.validTypeFiles.push(f);
          } else {
            acc.invalidTypeFiles.push(f.name);
          }
          return acc;
        },
        { validTypeFiles: [] as File[], invalidTypeFiles: [] as string[] }
      );
      filesToUpload = validTypeFiles;

      invalidTypeErrors.forEach((f) => {
        errorText += `${f} is not a valid type. Accepted formats include ${Object.keys(EXTENSION_TO_CONTENT_TYPE).join(', ')}.\n`;
      });

      const { validSizeFiles, invalidSizeFiles } = filesToUpload.reduce(
        (acc, f) => {
          if (f.size <= maxFileSizeMb) {
            acc.validSizeFiles.push(f);
          } else {
            acc.invalidSizeFiles.push(f.name);
          }
          return acc;
        },
        { validSizeFiles: [] as File[], invalidSizeFiles: [] as string[] }
      );

      filesToUpload = validSizeFiles;

      invalidSizeFiles.forEach((f) => {
        errorText += `${f} is too large. Files must be less than ${maxFileSizeMb / 1024 / 1024}MB.\n`;
      });

      const newFiles = filesToUpload.map((f) => ({
        fileName: f.name,
        fileSize: f.size,
        contentType: f.type,
      }));
      setValue(multiple ? initialValue.concat(newFiles) : newFiles);
      if (filesToUpload.length > 0 && errorText) {
        setError(errorText);
      } else if (filesToUpload.length === 0) {
        setError(errorText);
        return;
      }

      setIsUploading(true);
      try {
        const uploaded: UploadedFileMeta[] = [];

        /**
         * BATCH UPLOAD vs REGULAR UPLOAD:
         *
         * BATCH UPLOAD (preferred):
         * - Uploads ALL files in a SINGLE operation via onBatchUpload()
         * - Makes ONE API call regardless of file count
         * - More efficient for multiple files (reduced network overhead)
         * - Better progress tracking and error handling at the batch level
         * - Only available in standalone mode when onBatchUpload is provided
         *
         * REGULAR UPLOAD (fallback):
         * - Uploads files SEQUENTIALLY one at a time
         * - Makes MULTIPLE API calls (one per file)
         * - Used when batch upload is unavailable or in form mode
         * - Simpler implementation but slower for multiple files
         * - Individual file error handling (partial success possible)
         */

        // Use batch upload if available for any number of files (including 1)
        if (isStandalone && standalone?.onBatchUpload) {
          try {
            const results = await standalone.onBatchUpload(filesToUpload);
            uploaded.push(...results);
          } catch (err: unknown) {
            console.error('Error batch uploading files:', err);
            const errorMessage =
              err instanceof Error ? err.message : 'Error uploading files. Please try again.';
            setError((prev) => (prev ? prev + `\n${errorMessage}` : errorMessage));
          }
        } else {
          // Sequential upload fallback (when batch upload not available)
          for (const f of filesToUpload) {
            try {
              const result = await uploadFunction(f);
              uploaded.push(result);
            } catch (err: unknown) {
              console.error('Error uploading file:', err);
              setError((prev) => (prev ? prev + `\nError uploading ${f.name}` : `Error uploading ${f.name}`));
            }
          }
        }

        if (uploaded.length > 0) {
          const newValue = initialValue.concat(uploaded);
          setValue(multiple ? newValue : [uploaded[0]]);
          onChangeCallback?.(multiple ? newValue : uploaded[0]);

          // Call onUploadSuccess for standalone mode
          if (isStandalone && standalone?.onUploadSuccess) {
            standalone.onUploadSuccess();
          }
        }
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error uploading file(s). Please try again.';
        setError(errorMessage);
      } finally {
        setIsUploading(false);
      }
    },
    [value, maxFileSizeMb, uploadFunction, onChangeCallback, multiple, isStandalone, standalone]
  );

  // Drag and drop hook for visual feedback
  const { isDragging, dragHandlers } = useFileDragDrop({
    onDrop: (files) => {
      const filesToUpload = Array.isArray(files) ? files : [files];
      if (multiple) {
        doUpload(filesToUpload);
      } else {
        doUpload(filesToUpload[0]);
      }
    },
    multiple,
    disabled: disabled || isUploading,
  });

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.[0]) {
        if (multiple) {
          doUpload(Array.from(event.target.files));
        } else {
          doUpload(event.target.files[0]);
        }
      }
    },
    [doUpload, multiple]
  );

  const onRemoveFile = useCallback(
    (file: UploadedFileMeta) => {
      const newValue = value.filter((f) => f.fileName !== file.fileName);
      if (newValue.length === 0) {
        setError(null);
      }
      setValue(newValue);
      if (multiple) {
        onChangeCallback?.(newValue);
      } else {
        onChangeCallback?.(null);
      }
    },
    [multiple, onChangeCallback, value]
  );

  const onRemoveAll = useCallback(() => {
    setError(null);
    setValue([]);
    if (multiple) {
      onChangeCallback?.([]);
    } else {
      onChangeCallback?.(null);
    }
  }, [multiple, onChangeCallback]);

  // Render minimal variant if specified
  if (isMinimal) {
    return (
      <div>
        <Stack
          {...dragHandlers}
          alignItems='center'
          justifyContent='center'
          sx={{
            background: isDragging
              ? alpha(theme.colors.primary[60], 0.08)
              : alpha(theme.colors.primary[60], 0.04),
            border: `1px dashed ${isDragging ? theme.palette.primary.main : colors.neutral[30]}`,
            borderRadius: '4px',
            padding: '10px 24px',
            transition: 'all 0.2s ease-in-out',
            cursor: disabled ? 'default' : 'pointer',
            position: 'relative',
          }}
        >
          <input
            disabled={disabled || isUploading}
            multiple={multiple}
            data-testid='file-upload-field'
            type='file'
            onChange={handleFileChange}
            accept={Object.values(EXTENSION_TO_CONTENT_TYPE).join(',')}
            style={{
              opacity: 0,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              cursor: disabled ? 'default' : 'pointer',
            }}
          />
          <Box display='flex' alignItems='center' gap='4px'>
            {isUploading ? (
              <CircularProgress size={20} />
            ) : (
              <CloudUploadOutlinedIcon
                sx={{ color: alpha(theme.colors.neutral[70], 0.56), width: 20, height: 20 }}
              />
            )}
            <Typography variant='body2' sx={{ fontSize: '14px', lineHeight: '1.43' }}>
              {isUploading ? (
                'Uploading...'
              ) : (
                <>
                  Drag &amp; Drop or <span style={{ color: theme.colors.primary[60] }}>Browse files</span>
                </>
              )}
            </Typography>
          </Box>
        </Stack>
        {error && (
          <Typography
            variant='caption'
            component='p'
            sx={{ color: colors.critical[60], mt: 1 }}
            whiteSpace='pre-wrap'
          >
            {error}
          </Typography>
        )}
      </div>
    );
  }

  return (
    <>
      <div>
        <Stack
          {...dragHandlers}
          direction='row'
          alignItems='center'
          justifyContent='center'
          sx={{
            background: isDragging
              ? alpha(theme.colors.primary[60], 0.08)
              : alpha(theme.colors.primary[60], 0.04),
            border: `1px dashed ${isDragging ? theme.palette.primary.main : colors.neutral[30]}`,
            borderRadius: '4px',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <InputFileWrapper style={{ justifyContent: value.length ? 'stretch' : 'center' }}>
            <input
              disabled={disabled}
              multiple={multiple}
              data-testid='file-upload-field'
              type='file'
              onChange={handleFileChange}
              accept={Object.values(EXTENSION_TO_CONTENT_TYPE).join(',')}
            />
            {disabled && value.length === 0 ? (
              <Stack gap='0.5rem' direction='row' alignItems='center' justifyContent='center'>
                <Typography color='text.secondary'>No Files</Typography>
              </Stack>
            ) : (
              <Stack>
                {!disabled && (
                  <Stack gap='0.5rem' mb='1.5rem' alignItems='center' justifyContent='center'>
                    <Box display='flex' alignItems='center' gap='0.5rem' justifyContent='center'>
                      <CloudUploadOutlinedIcon
                        sx={{ color: alpha(theme.colors.neutral[70], 0.56), mr: '0.5rem' }}
                      />
                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.primary',
                        }}
                      >
                        Drag &amp; Drop or <span style={{ color: colors.neutral[50] }}>Browse files</span>
                      </Typography>
                    </Box>
                    <Typography variant='body2' sx={{ color: alpha(theme.colors.primary[100], 0.5) }}>
                      {Object.keys(EXTENSION_TO_CONTENT_TYPE)
                        .map((ext) => ext.toUpperCase())
                        .join(', ')}{' '}
                      formats, up to {(maxFileSizeMb / 1024 / 1024).toFixed(1)}MB
                    </Typography>
                  </Stack>
                )}
                <SelectedFiles
                  companyId={companyId}
                  files={value}
                  fieldLabel={fieldLabel}
                  isUploading={isUploading}
                  onRemoveFile={onRemoveFile}
                  readOnly={disabled}
                />
                {value.length > 1 && !isUploading && !disabled && (
                  <Button
                    onClick={onRemoveAll}
                    sx={{
                      placeSelf: 'end',
                      width: 'fit-content',
                      marginBottom: '-.5rem',
                    }}
                    color='secondary'
                    {...preventDefaults}
                  >
                    {'Remove all'}
                  </Button>
                )}
              </Stack>
            )}
          </InputFileWrapper>
        </Stack>

        {error && (
          <Typography
            variant='caption'
            component={'p'}
            sx={{ color: colors.critical[60] }}
            whiteSpace={'pre-wrap'}
          >
            {error}
          </Typography>
        )}
      </div>
    </>
  );
}

function FileTitle({ name, size }: { name: string; size: number }) {
  const { colors } = useTheme();

  return (
    <Box display='flex' gap='0.5rem' alignItems={'center'}>
      <Typography variant='body2' sx={{ color: colors.neutral[70] }}>
        {name}
      </Typography>
      <Typography color='text.secondary' variant='body2'>
        {convertFileSize(size ?? 0)}
      </Typography>
    </Box>
  );
}

function SelectedFiles({
  companyId,
  files: files,
  fieldLabel,
  isUploading,
  onRemoveFile,
  readOnly,
}: {
  companyId?: number;
  fieldLabel?: string;
  files: UploadedFileMeta[];
  onRemoveFile: (file: UploadedFileMeta) => void;
  isUploading?: boolean;
  readOnly?: boolean;
}) {
  const getFileName = useResponseFileName();
  async function handleDownload(f: UploadedFileMeta) {
    if (!f?.fileId) {
      return;
    }

    // If no companyId, try to use the file's getUrl directly
    if (!companyId) {
      if (f.getUrl) {
        window.open(f.getUrl);
      }
      return;
    }

    const getUrl = await fetchKPIRequestUploadedFileFromS3({
      companyId,
      fileId: f?.fileId as string,
      contentType: f?.contentType,
      fileName: fieldLabel ? getFileName(fieldLabel, f?.fileName) : getFileName(f?.fileName),
    });
    window.open(getUrl);
  }
  return (
    <Box width='100%'>
      {files.map((f) => {
        return (
          <Stack direction={'row'} alignItems='center' justifyContent='space-between' key={f.fileName}>
            <Stack direction={'row'} gap='.5rem' justifyContent={'center'} minHeight={'2rem'}>
              <InsertDriveFileOutlinedIcon sx={{ color: 'text.secondary' }} fontSize='medium' />
              <Button onClick={() => handleDownload(f)} {...preventDefaults}>
                <FileTitle name={f.fileName} size={f.fileSize} />
              </Button>
            </Stack>
            {!readOnly && (
              <IconButton
                title='remove'
                size='small'
                onClick={(e) => {
                  e.preventDefault();
                  onRemoveFile(f);
                }}
                disabled={isUploading}
                {...preventDefaults}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            )}
          </Stack>
        );
      })}

      <>
        {isUploading ? (
          <LinearProgress color={'primary'} sx={{ width: '100%', px: '.25rem' }} />
        ) : (
          <div style={{ height: '4px' }} />
        )}
      </>
    </Box>
  );
}

// Re-export for backward compatibility
export const allowedExtensions = ALLOWED_EXTENSIONS;
export const ExtensionToContentType = EXTENSION_TO_CONTENT_TYPE;
